import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Download, FileText, Upload, Check } from 'lucide-react';
import { TermsAndConditions } from './policies/TermsAndConditions';
import { PrivacyPolicy } from './policies/PrivacyPolicy';
import { CookiePolicy } from './policies/CookiePolicy';
import { TradesmanTerms } from './policies/TradesmanTerms';
import { CustomerTerms } from './policies/CustomerTerms';
import { AcceptableUsePolicy } from './policies/AcceptableUsePolicy';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import jsPDF from 'jspdf';

interface Document {
  id: string;
  title: string;
  description: string;
  component: React.ComponentType;
}

const documents: Document[] = [
  {
    id: 'terms-conditions',
    title: 'Terms and Conditions',
    description: 'General terms and conditions for using Sortable',
    component: TermsAndConditions,
  },
  {
    id: 'privacy-policy',
    title: 'Privacy Policy',
    description: 'How we collect, use, and protect your personal information',
    component: PrivacyPolicy,
  },
  {
    id: 'cookie-policy',
    title: 'Cookie Policy',
    description: 'Information about how we use cookies on our platform',
    component: CookiePolicy,
  },
  {
    id: 'tradesman-terms',
    title: 'Terms for Tradespeople',
    description: 'Specific terms and requirements for tradespeople',
    component: TradesmanTerms,
  },
  {
    id: 'customer-terms',
    title: 'Terms for Customers',
    description: 'Specific terms and guidelines for customers',
    component: CustomerTerms,
  },
  {
    id: 'acceptable-use',
    title: 'Acceptable Use Policy',
    description: 'Guidelines for appropriate platform usage',
    component: AcceptableUsePolicy,
  },
];

export const LegalDocumentDownloader: React.FC = () => {
  const { toast } = useToast();
  const [uploadedDocuments, setUploadedDocuments] = useState<{[key: string]: string}>({});
  const [uploading, setUploading] = useState<{[key: string]: boolean}>({});
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkAdminStatus();
    loadUploadedDocuments();
  }, []);

  const checkAdminStatus = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user?.email === 'realise@post.com') {
      setIsAdmin(true);
    }
  };

  const loadUploadedDocuments = async () => {
    try {
      const { data, error } = await supabase.storage
        .from('legal-documents')
        .list('', { limit: 100 });

      if (error) throw error;

      const documentMap: {[key: string]: string} = {};
      data?.forEach((file) => {
        const docId = file.name.replace('.pdf', '');
        if (documents.find(d => d.id === docId)) {
          documentMap[docId] = file.name;
        }
      });
      setUploadedDocuments(documentMap);
    } catch (error) {
      console.error('Error loading uploaded documents:', error);
    }
  };

  const handleFileUpload = async (doc: Document, file: File) => {
    if (!file.type.includes('pdf')) {
      toast({
        title: "Invalid file type",
        description: "Please upload a PDF file.",
        variant: "destructive"
      });
      return;
    }

    console.log('Starting file upload for:', doc.id, 'File size:', file.size);
    setUploading(prev => ({ ...prev, [doc.id]: true }));

    try {
      const fileName = `${doc.id}.pdf`;
      
      console.log('Attempting to upload to bucket: legal-documents, fileName:', fileName);
      
      // Delete existing file if it exists
      const { error: deleteError } = await supabase.storage
        .from('legal-documents')
        .remove([fileName]);
      
      if (deleteError) {
        console.log('Delete error (may be expected if file doesn\'t exist):', deleteError);
      }

      // Upload new file
      const { data, error } = await supabase.storage
        .from('legal-documents')
        .upload(fileName, file, {
          upsert: true
        });

      console.log('Upload result:', { data, error });

      if (error) {
        console.error('Upload error details:', error);
        throw error;
      }

      console.log('Upload successful, path:', data.path);

      toast({
        title: "Upload successful",
        description: `${doc.title} has been uploaded successfully.`
      });

      await loadUploadedDocuments();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: "Upload failed",
        description: `Error: ${error.message || 'Unknown error occurred'}`,
        variant: "destructive"
      });
    } finally {
      setUploading(prev => ({ ...prev, [doc.id]: false }));
    }
  };

  const downloadDocument = async (doc: Document) => {
    try {
      // Check if there's an uploaded version
      if (uploadedDocuments[doc.id]) {
        const { data, error } = await supabase.storage
          .from('legal-documents')
          .createSignedUrl(uploadedDocuments[doc.id], 60);

        if (error) throw error;

        // Create a temporary link to download the file
        const link = document.createElement('a');
        link.href = data.signedUrl;
        link.download = `${doc.id}-sortable.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      // Fallback to generated PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Add company header
      pdf.setFontSize(20);
      pdf.text(doc.title, 20, 30);
      
      pdf.setFontSize(12);
      pdf.text('Realise Property Maintenance Ltd. trading as "Sortable"', 20, 45);
      pdf.text('13 Harbury Rd. Henleaze Business Centre, Henleaze, Bristol, BS9 4PN', 20, 52);
      pdf.text('Telephone: 08000438880', 20, 59);
      pdf.text(`Last Updated: ${new Date().toLocaleDateString()}`, 20, 66);
      
      pdf.setFontSize(10);
      pdf.text('For complete terms and policies, please visit our website.', 20, 80);
      pdf.text('This is a simplified version for quick reference.', 20, 87);
      
      // Add basic content
      pdf.text('Please refer to our website for the complete and current version', 20, 100);
      pdf.text('of this document with all detailed terms and conditions.', 20, 107);
      
      // Download the PDF
      pdf.save(`${doc.id}-sortable.pdf`);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast({
        title: "Download failed",
        description: "There was an error downloading the document.",
        variant: "destructive"
      });
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
          <FileText className="h-4 w-4 mr-2" />
          Legal Documents
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Legal Documents
          </DialogTitle>
          <DialogDescription>
            Download our legal documents and policies in PDF format
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-4">
            {documents.map((doc) => (
              <div key={doc.id} className="p-4 border rounded-lg space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{doc.title}</h3>
                      {uploadedDocuments[doc.id] && (
                        <Check className="h-4 w-4 text-green-600" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{doc.description}</p>
                    {uploadedDocuments[doc.id] && (
                      <p className="text-xs text-green-600 mt-1">Custom PDF uploaded</p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadDocument(doc)}
                    className="ml-4"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                </div>
                
                {isAdmin && (
                  <div className="border-t pt-3">
                    <Label htmlFor={`upload-${doc.id}`} className="text-sm font-medium">
                      Upload Custom PDF (Admin Only)
                    </Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        id={`upload-${doc.id}`}
                        type="file"
                        accept=".pdf"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleFileUpload(doc, file);
                          }
                        }}
                        disabled={uploading[doc.id]}
                        className="flex-1"
                      />
                      {uploading[doc.id] && (
                        <div className="text-sm text-muted-foreground">Uploading...</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="text-xs text-muted-foreground pt-4 border-t">
            <p>
              <strong>Company:</strong> Realise Property Maintenance Ltd. trading as "Sortable"<br />
              <strong>Address:</strong> 13 Harbury Rd. Henleaze Business Centre, Henleaze, Bristol, BS9 4PN<br />
              <strong>Telephone:</strong> 08000438880
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};